import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getKitchenStations,
  getKitchenOrders,
  startPreparingItem as apiStartItem,
  completeItem as apiCompleteItem,
  resetItem as apiResetItem,
  bumpOrder as apiBumpOrder,
  recallOrder as apiRecallOrder
} from '../services/api';
import { toast } from '../lib/toast';

// Derive an order's status from its items — mirrors the backend's rules so
// optimistic updates match what a refetch will reconcile to.
const computeOrderStatus = (items) => {
  if (!items || items.length === 0) return 'pending';
  if (items.every((i) => i.status === 'ready')) return 'ready';
  if (items.some((i) => i.status === 'preparing' || i.status === 'ready')) return 'preparing';
  return 'pending';
};

const patchItemStatus = (orders, orderId, itemId, status) =>
  orders.map((order) => {
    if (order.id !== orderId) return order;
    const items = order.items.map((item) =>
      item.id === itemId ? { ...item, status } : item
    );
    return { ...order, items, status: computeOrderStatus(items) };
  });

const useKitchenOrders = (restaurantId, selectedStationIds = []) => {
  const { t } = useTranslation();
  const [allOrders, setAllOrders] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const didInitialLoad = useRef(false);
  // Always-current snapshot of orders, so optimistic rollback uses fresh state.
  const allOrdersRef = useRef(allOrders);
  allOrdersRef.current = allOrders;

  const stationKey = selectedStationIds.join(',');

  // Station filtering is derived client-side: keep every order in state and
  // show only those with an item at a selected station.
  const orders = useMemo(() => {
    if (!selectedStationIds.length) return allOrders;
    const set = new Set(selectedStationIds);
    return allOrders.filter((order) =>
      order.items?.some((item) => item.station && set.has(item.station.id))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders, stationKey]);

  const fetchStations = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await getKitchenStations(restaurantId);
      setStations(res.data?.stations || []);
    } catch (e) {
      console.error('Fetch stations error:', e);
    }
  }, [restaurantId]);

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) {
      setAllOrders([]);
      setLoading(false);
      return;
    }
    // First load blocks with a spinner; later refetches stay silent so the
    // order grid never blanks out under the cook.
    if (didInitialLoad.current) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await getKitchenOrders(restaurantId);
      setAllOrders(res.data?.orders || []);
      setError(null);
    } catch (e) {
      console.error('Fetch orders error:', e);
      setError(e.message || 'Failed to load orders');
    } finally {
      didInitialLoad.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const refreshOrders = useCallback(() => fetchOrders(), [fetchOrders]);

  // Apply a local mutation immediately, fire the API call, then reconcile with
  // a background refetch. On failure, roll back and tell the cook.
  const runOptimistic = useCallback(
    async (mutator, apiCall) => {
      const snapshot = allOrdersRef.current;
      setAllOrders(mutator(snapshot));
      try {
        await apiCall();
        fetchOrders();
      } catch (e) {
        console.error('Kitchen action failed:', e);
        setAllOrders(snapshot);
        toast.error(t('kitchen.actionFailed'));
      }
    },
    [fetchOrders, t]
  );

  const startItem = useCallback(
    (orderId, itemId, stationId) =>
      runOptimistic(
        (prev) => patchItemStatus(prev, orderId, itemId, 'preparing'),
        () => apiStartItem(orderId, itemId, stationId)
      ),
    [runOptimistic]
  );

  const completeItem = useCallback(
    (orderId, itemId, stationId) =>
      runOptimistic(
        (prev) => patchItemStatus(prev, orderId, itemId, 'ready'),
        () => apiCompleteItem(orderId, itemId, stationId)
      ),
    [runOptimistic]
  );

  const resetItem = useCallback(
    (orderId, itemId, stationId) =>
      runOptimistic(
        (prev) => patchItemStatus(prev, orderId, itemId, 'pending'),
        () => apiResetItem(orderId, itemId, stationId)
      ),
    [runOptimistic]
  );

  const bumpOrder = useCallback(
    (orderId) =>
      runOptimistic(
        (prev) => prev.filter((o) => o.id !== orderId),
        () => apiBumpOrder(orderId)
      ),
    [runOptimistic]
  );

  // A recalled order is not in local state (it was bumped away), so just
  // refetch to pull it back onto the board.
  const recallOrder = useCallback(
    async (orderId) => {
      try {
        await apiRecallOrder(orderId);
        await fetchOrders();
      } catch (e) {
        console.error('Recall order error:', e);
        toast.error(t('kitchen.recallFailed'));
      }
    },
    [fetchOrders, t]
  );

  return {
    orders,
    stations,
    loading,
    refreshing,
    error,
    refreshOrders,
    startItem,
    completeItem,
    resetItem,
    bumpOrder,
    recallOrder
  };
};

export default useKitchenOrders;
